import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnamesBind from 'classnames/bind';
import Icon from '@concur/nui-widgets/lib/Icon/Icon';
import List from '@concur/nui-widgets/lib/List/List';
import ListItem from '@concur/nui-widgets/lib/List/_ListItem';
import { withThemeStyles } from '@concur/react-ui-theming';
import { withFormatter } from '@concur/nui-intl-runtime';
import { withErrorBoundary, compose } from '@concur/core-ui-shell';
import ProfileIdentifier from './_ProfileIdentifier';
import ActingAsFiori from './_ActingAsFiori';
import SignOutSvgIcon from './_SignOutSvgIcon';
import * as styles from './UserProfileMenu-*.css';

const CSS_BLOCK = 'sapcnqr-user-profile-menu';

const UserProfileMenuFiori = ({
    actingAs,
    classNameMap,
    formatter,
    isGov,
    logoutUser,
    onClose,
    onEndLoading,
    onEndSession,
    onStartLoading,
    onStartSession,
    profileUrl,
}) => {
    const classnames = classnamesBind.bind(classNameMap);

    const { allUsers, companyName, otherUserName } = actingAs;
    const isActingForOthers = !!(allUsers || companyName || otherUserName);

    const [actingAsExpanded, setIsActingAsExpanded] = useState(isActingForOthers);

    const handleSelect = (eventKey, e, liRef) => {
        const actingAsTrigger = document.querySelector(`[class*="${CSS_BLOCK}__acting-as-trigger"]`);

        switch (eventKey) {
        case 'acting-as':
            if (e.target === liRef || actingAsTrigger?.contains(e.target)) {
                setIsActingAsExpanded(!actingAsExpanded);
            }
            break;
        case 'sign-out':
            logoutUser?.();
            break;
        default:
        }
    };

    return (
        <div
            className={classnames(CSS_BLOCK)}
            data-test="user-profile-menu"
        >
            <ProfileIdentifier
                actingAs={actingAs}
                classNameMap={classNameMap}
                cssBlock={CSS_BLOCK}
            />
            <List
                aria-label={formatter.formattedMessage({ id: 'CoreUI.profile.profileMenu' })}
                disableFocusManager
                onSelect={handleSelect}
            >
                {(actingAs.options?.length > 0 || isActingForOthers) && (
                    <ListItem
                        aria-label={!actingAsExpanded ? formatter.formattedMessage({ id: 'CoreUI.actingAsExpand' })
                            : formatter.formattedMessage({ id: 'CoreUI.actingAsCollapse' })}
                        aria-expanded={actingAsExpanded}
                        data-test="user-profile-menu-acting-as"
                        eventKey="acting-as"
                        itemClassName={classnames(`${CSS_BLOCK}__acting-as`)}
                    >
                        <div
                            className={classnames(`${CSS_BLOCK}__acting-as-trigger`)}
                        >
                            <Icon
                                ariaHidden
                                className={classnames(`${CSS_BLOCK}__icon`)}
                                iconName="shuffle"
                                size="md"
                            />
                            {formatter.formattedMessage({ id: 'CoreUI.actAsAnotherUser' })}
                            <Icon
                                ariaHidden
                                className={classnames(`${CSS_BLOCK}__acting-as-trigger-icon`)}
                                iconName={actingAsExpanded ? 'arrow-3-n' : 'arrow-3-s'}
                                size="md"
                            />
                        </div>
                    </ListItem>
                )}
                {actingAsExpanded && (
                    <ListItem
                        data-test="user-profile-menu-acting-as-content"
                        itemClassName={classnames(`${CSS_BLOCK}__acting-as-content`)}
                        aria-expanded={actingAsExpanded}
                    >
                        <ActingAsFiori
                            isActingForOthers={isActingForOthers}
                            isGov={isGov}
                            onClose={onClose}
                            onEndLoading={onEndLoading}
                            onEndSession={onEndSession}
                            onStartLoading={onStartLoading}
                            onStartSession={onStartSession}
                            options={actingAs.options}
                        />
                    </ListItem>
                )}
                <ListItem
                    data-test="user-profile-menu-profile-link"
                    disabled={!profileUrl || profileUrl.trim() === '#'}
                    itemClassName={classnames(`${CSS_BLOCK}__list-item-content`)}
                    url={profileUrl !== '#' ? profileUrl : undefined}
                    eventKey="profile-settings"
                >
                    <Icon ariaHidden className={classnames(`${CSS_BLOCK}__icon`)} iconName="gear" size="md" />
                    {formatter.formattedMessage({ id: 'CoreUI.profile.profileSettings' })}
                </ListItem>
                <ListItem
                    data-test="user-profile-menu-signout-link"
                    eventKey="sign-out"
                    itemClassName={classnames(`${CSS_BLOCK}__list-item-content`)}
                >
                    <SignOutSvgIcon
                        className={classnames(`${CSS_BLOCK}__list-item-content-svg`)}
                        pathClassName={classnames(`${CSS_BLOCK}__list-item-content-path`)}
                    />
                    {formatter.formattedMessage({ id: 'CoreUI.signOut' })}
                </ListItem>
            </List>
        </div>
    );
};

UserProfileMenuFiori.displayName = 'UserProfileMenuFiori';

UserProfileMenuFiori.cssBlock = CSS_BLOCK;

UserProfileMenuFiori.propTypes = {
    actingAs: PropTypes.shape({
        allUsers: PropTypes.bool,
        companyName: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.string),
        otherUserName: PropTypes.string,
    }),
    isGov: PropTypes.bool,
    logoutUser: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onEndSession: PropTypes.func,
    onEndLoading: PropTypes.func,
    onStartLoading: PropTypes.func,
    onStartSession: PropTypes.func,
    profileUrl: PropTypes.string,
};

UserProfileMenuFiori.defaultProps = {
    actingAs: {},
    profileUrl: '#',
};

export default compose(
    withThemeStyles(styles),
    withFormatter,
    withErrorBoundary,
)(UserProfileMenuFiori);
